import React from 'react';
import { UserProfile } from '../../types/advanced-retirement';
import FormattedInput from '../FormattedInput';

interface InvestmentSettingsSectionProps {
  userProfile: UserProfile;
  handleInputChange: (field: keyof UserProfile, value: any) => void;
}

const InvestmentSettingsSection: React.FC<InvestmentSettingsSectionProps> = ({
  userProfile,
  handleInputChange
}) => {
  return (
    <div className="form-section">
      <h3>Investment Settings</h3>
      <div className="form-grid">
        {/* RRSP/RRIF Section */}
        <div className="form-group full-width">
          <label>RRSP/RRIF</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Return Rate (%)</small>
              <FormattedInput
                defaultText="Default: 5.5%"
                value={userProfile.rrspReturnRate}
                onChange={(value) => {
                  handleInputChange('rrspReturnRate', value as number);
                  // Also update RRIF rate to match
                  handleInputChange('rrifReturnRate', value as number);
                }}
                min={-20}
                max={20}
                step={0.1}
                formatOptions={{ type: 'percent', decimalPlaces: 2 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Return Rate (%)</small>
                <FormattedInput
                  defaultText="Default: 5.5%"
                  value={(userProfile.spouseRrspReturnRate || userProfile.rrspReturnRate)}
                  onChange={(value) => {
                    handleInputChange('spouseRrspReturnRate', value as number);
                    // Also update spouse RRIF rate to match
                    handleInputChange('spouseRrifReturnRate', value as number);
                  }}
                  min={-20}
                  max={20}
                  step={0.1}
                  formatOptions={{ type: 'percent', decimalPlaces: 2 }}
                />
              </div>
            )}
          </div>
          <div>&nbsp;</div>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Risk Level (1-5)</small>
              <input 
                type="number" 
                value={userProfile.rrspRiskLevel}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  handleInputChange('rrspRiskLevel', value);
                  // Also update RRIF risk level to match
                  handleInputChange('rrifRiskLevel', value);
                }}
                min="1" max="5"
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Risk Level (1-5)</small>
                <input 
                  type="number" 
                  value={(userProfile.spouseRrspRiskLevel || userProfile.rrspRiskLevel)}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    handleInputChange('spouseRrspRiskLevel', value);
                    // Also update spouse RRIF risk level to match
                    handleInputChange('spouseRrifRiskLevel', value);
                  }}
                  min="1" max="5"
                />
              </div>
            )}
          </div>
        </div>

        {/* TFSA Section */}
        <div className="form-group full-width">
          <label>TFSA</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Return Rate (%)</small>
              <FormattedInput
                defaultText="Default: 6.5%"
                value={userProfile.tfsaReturnRate}
                onChange={(value) => handleInputChange('tfsaReturnRate', value as number)}
                min={-20}
                max={20}
                step={0.1}
                formatOptions={{ type: 'percent', decimalPlaces: 2 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Return Rate (%)</small>
                <FormattedInput
                  defaultText="Default: 6.5%"
                  value={(userProfile.spouseTfsaReturnRate || userProfile.tfsaReturnRate)}
                  onChange={(value) => handleInputChange('spouseTfsaReturnRate', value as number)}
                  min={-20}
                  max={20}
                  step={0.1}
                  formatOptions={{ type: 'percent', decimalPlaces: 2 }}
                />
              </div>
            )}
          </div>
          <div>&nbsp;</div>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Risk Level (1-5)</small>
              <input 
                type="number" 
                value={userProfile.tfsaRiskLevel}
                onChange={(e) => handleInputChange('tfsaRiskLevel', parseInt(e.target.value))}
                min="1" max="5"
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Risk Level (1-5)</small>
                <input 
                  type="number" 
                  value={(userProfile.spouseTfsaRiskLevel || userProfile.tfsaRiskLevel)}
                  onChange={(e) => handleInputChange('spouseTfsaRiskLevel', parseInt(e.target.value))}
                  min="1" max="5"
                />
              </div>
            )}
          </div>
        </div>

        {/* Other Investments Section */}
        <div className="form-group full-width">
          <label>Other Investments</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Return Rate (%)</small>
              <FormattedInput
                defaultText="Default: 7.0%"
                value={userProfile.nonRegisteredReturnRate}
                onChange={(value) => handleInputChange('nonRegisteredReturnRate', value as number)}
                min={-20}
                max={20}
                step={0.1}
                formatOptions={{ type: 'percent', decimalPlaces: 2 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Return Rate (%)</small>
                <FormattedInput
                  defaultText="Default: 7.0%"
                  value={(userProfile.spouseNonRegisteredReturnRate || userProfile.nonRegisteredReturnRate)}
                  onChange={(value) => handleInputChange('spouseNonRegisteredReturnRate', value as number)}
                  min={-20}
                  max={20}
                  step={0.1}
                  formatOptions={{ type: 'percent', decimalPlaces: 2 }}
                />
              </div>
            )}
          </div>
          <div>&nbsp;</div>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Risk Level (1-5)</small>
              <input 
                type="number" 
                value={userProfile.nonRegisteredRiskLevel}
                onChange={(e) => handleInputChange('nonRegisteredRiskLevel', parseInt(e.target.value))}
                min="1" max="5"
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Risk Level (1-5)</small>
                <input 
                  type="number" 
                  value={(userProfile.spouseNonRegisteredRiskLevel || userProfile.nonRegisteredRiskLevel)}
                  onChange={(e) => handleInputChange('spouseNonRegisteredRiskLevel', parseInt(e.target.value))}
                  min="1" max="5"
                />
              </div>
            )}
          </div>
        </div>

        {/* Corporate Section */}
        <div className="form-group full-width">
          <label>Corporate</label>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Return Rate (%)</small>
              <FormattedInput
                defaultText="Default: 8.0%"
                value={userProfile.corporateReturnRate}
                onChange={(value) => handleInputChange('corporateReturnRate', value as number)}
                min={-20}
                max={20}
                step={0.1}
                formatOptions={{ type: 'percent', decimalPlaces: 2 }}
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Return Rate (%)</small>
                <FormattedInput
                  defaultText="Default: 8.0%"
                  value={(userProfile.spouseCorporateReturnRate || userProfile.corporateReturnRate)}
                  onChange={(value) => handleInputChange('spouseCorporateReturnRate', value as number)}
                  min={-20}
                  max={20}
                  step={0.1}
                  formatOptions={{ type: 'percent', decimalPlaces: 2 }}
                />
              </div>
            )}
          </div>
          <div>&nbsp;</div>
          <div className="side-by-side-inputs">
            <div className="input-group">
              <small>Risk Level (1-5)</small>
              <input 
                type="number" 
                value={userProfile.corporateRiskLevel}
                onChange={(e) => handleInputChange('corporateRiskLevel', parseInt(e.target.value))}
                min="1" max="5"
              />
            </div>
            {userProfile.spouseAge && (
              <div className="input-group">
                <small>Spouse Risk Level (1-5)</small>
                <input 
                  type="number" 
                  value={(userProfile.spouseCorporateRiskLevel || userProfile.corporateRiskLevel)}
                  onChange={(e) => handleInputChange('spouseCorporateRiskLevel', parseInt(e.target.value))}
                  min="1" max="5"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentSettingsSection; 